// «Seguridad de la cuenta» del personal clínico (MT-043-C): estado de la verificación en dos pasos, activarla por voluntad propia mientras aún no es
// obligatoria, y pedir códigos de respaldo nuevos. Sin <TopNav>/<FarmaciaApp> a propósito (misma razón que MiCertificadoPage): la ven DOS roles con
// navs distintos; un enlace simple desde cada uno trae aquí. El servidor decide todo (la cuenta sale de la sesión del proxy).
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { sesionClinica, type CodigosRespaldo, type EstadoMfa } from '@/lib/hceMfa';
import { fechaDePolitica } from './politicaMfaTexto';
import { PasoEnrolamiento, VistaCodigosRespaldo } from './PasosMfa';

type Vista = 'estado' | 'activar-clave' | 'activar' | 'respaldo-pedir' | 'respaldo-nuevos';

export function SeguridadCuentaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [estado, setEstado] = useState<EstadoMfa | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vista, setVista] = useState<Vista>('estado');
  const [codigo, setCodigo] = useState('');
  const [clave, setClave] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [nuevos, setNuevos] = useState<CodigosRespaldo | null>(null);

  const cargar = useCallback(async () => {
    const r = await sesionClinica.estado();
    if (r.ok) setEstado(r);
    else setError(r.status === 401 ? t('seguridad.sesionVencida') : r.error);
  }, [t]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function pedirRespaldoNuevo(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    const r = await sesionClinica.regenerarRespaldo(codigo.trim());
    setEnviando(false);
    setCodigo('');
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setNuevos({ backupCodes: r.backupCodes, backupCodesPdf: r.backupCodesPdf });
    setVista('respaldo-nuevos');
  }

  const politica = estado ? fechaDePolitica(estado.politica) : null;
  const iniciarActivacion = useCallback(() => sesionClinica.activarIniciar(clave), [clave]);

  return (
    <div className="min-h-screen bg-hce-paper px-4 py-8">
      <div className="mx-auto max-w-md space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-medium text-hce-ink">{t('seguridad.titulo')}</h1>
          <Button variant="outline" size="sm" onClick={() => void navigate(-1)}>
            {t('seguridad.volver')}
          </Button>
        </div>

        {error && (
          <Alert variant="red">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!estado && !error && <p className="text-sm text-hce-muted">{t('seguridad.cargando')}</p>}

        {estado && vista === 'estado' && (
          <div className="space-y-3 rounded-md border border-border bg-hce-surface p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-hce-ink">{t('seguridad.dosPasos')}</span>
              <Badge variant={estado.activo ? 'sage' : 'clay'}>{estado.activo ? t('seguridad.activa') : t('seguridad.inactiva')}</Badge>
            </div>
            <p className="text-xs text-hce-muted">{t('seguridad.explicacion')}</p>
            {politica && <p className="text-xs text-hce-ink">{t(politica.clave, politica.valores)}</p>}
            {estado.activo ? (
              <>
                <p className="text-xs text-hce-muted">{t('seguridad.respaldoRestantes', { n: estado.respaldoRestantes })}</p>
                {estado.respaldoRestantes <= 3 && <p className="text-xs text-hce-clay">{t('seguridad.respaldoPocos')}</p>}
                <Button variant="outline" size="sm" onClick={() => { setError(null); setVista('respaldo-pedir'); }}>
                  {t('seguridad.respaldoNuevos')}
                </Button>
              </>
            ) : (
              <Button size="sm" onClick={() => { setError(null); setClave(''); setVista('activar-clave'); }}>
                {t('seguridad.activar')}
              </Button>
            )}
          </div>
        )}

        {estado && vista === 'activar-clave' && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (clave) setVista('activar');
            }}
            className="space-y-3 rounded-md border border-border bg-hce-surface p-4"
          >
            <p className="text-sm font-medium text-hce-ink">{t('seguridad.activar')}</p>
            <p className="text-xs text-hce-muted">{t('seguridad.pedirClave')}</p>
            <Input type="password" value={clave} onChange={(e) => setClave(e.target.value)} autoComplete="current-password" aria-label={t('auth.login.contrasena')} required />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={!clave}>
                {t('seguridad.admin.continuar')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setVista('estado')}>
                {t('auth.mfa.volver')}
              </Button>
            </div>
          </form>
        )}

        {estado && vista === 'activar' && (
          <div className="rounded-md border border-border bg-hce-surface p-4">
            <PasoEnrolamiento
              iniciar={iniciarActivacion}
              confirmar={(c) => sesionClinica.activarConfirmar(c, clave)}
              onCancelar={() => setVista('estado')}
              onTerminar={() => {
                setClave('');
                setVista('estado');
                void cargar();
              }}
            />
          </div>
        )}

        {estado && vista === 'respaldo-pedir' && (
          <form onSubmit={(e) => void pedirRespaldoNuevo(e)} className="space-y-3 rounded-md border border-border bg-hce-surface p-4">
            <p className="text-sm font-medium text-hce-ink">{t('seguridad.respaldoNuevos')}</p>
            <p className="text-xs text-hce-muted">{t('seguridad.respaldoNuevosAyuda')}</p>
            <Input value={codigo} onChange={(e) => setCodigo(e.target.value)} autoComplete="one-time-code" maxLength={64} aria-label={t('auth.mfa.codigo.etiqueta')} required />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={enviando || !codigo.trim()}>
                {enviando ? t('auth.mfa.codigo.verificando') : t('seguridad.respaldoGenerar')}
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setVista('estado')}>
                {t('auth.mfa.volver')}
              </Button>
            </div>
          </form>
        )}

        {estado && vista === 'respaldo-nuevos' && nuevos && (
          <div className="rounded-md border border-border bg-hce-surface p-4">
            <VistaCodigosRespaldo
              codigos={nuevos}
              onContinuar={() => {
                setNuevos(null);
                setVista('estado');
                void cargar();
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
